import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatError, MatFormField, MatFormFieldDefaultOptions, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { AraButtonComponent } from '@shared/ui/ara-button/ara-button.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatTooltip } from '@angular/material/tooltip';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import dayjs, { Dayjs } from 'dayjs';
import { ServiceCompanyService } from '@eadsysteme/ewsrealestateclient-v2';
import { Address, Person } from '@eadsysteme/ewsrealestateclient-v3';
import { RoleSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { Role, RoleType, UiRoleType } from '../../../../services/RoleModel';
import { SnackbarService } from '../../../../services/snackbar.service';
import { SessionService } from '../../../../services/session.service';
import { DateUtil } from '../../../../util/DateUtil';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_DATE_FORMATS } from '../../../../util/material-dayjs-adapter';
import { fadeInUp400ms } from '@shared/animations/fade-in-up.animation';
import { AraGenericDialogAction, AraGenericDialogComponent } from '@shared/ui/ara-generic-dialog/ara-generic-dialog.component';
import { AraInlineEditColumn, AraInlineEditRow, AraInlineEditTableComponent } from '@shared/ui/ara-inline-edit-table/ara-inline-edit-table.component';
import { AraPanelComponent } from '@shared/ui/ara-panel/ara-panel.component';
import { isProtectedRoleType, isSyntheticPersonId } from '../roles-view-model';

import { NO_UVI_RECEPTION_RIGHTS, UviReceptionRights } from '../uvi-reception-rights.service';
import { RolePortalAccess, RolePortalAccessService } from '../role-portal-access.service';
import { canHavePortalAccess } from '../../../../util/role-permission-type';
import { PersonRolesEditorService, TranslatedCountry } from './person-roles-editor.service';
import {
	PersonEditorDialogData,
	PersonEditorDialogResult,
	PersonEditorEntryContext,
	PersonEditorPersonOption,
	PersonEditorRoleValue,
	PortalAccessAction,
	StrandContext,
} from './person-editor-dialog.types';
import { StrandContextFactory } from './strand-context.factory';
import { StrandBoundaryService } from './strand-boundary.service';

import { AraCheckboxComponent } from '@shared/ui/ara-checkbox/ara-checkbox.component';
import { AraSlideToggleComponent } from '@shared/ui/ara-slide-toggle/ara-slide-toggle.component';
import CountryEnum = Address.CountryEnum;
import SalutationEnum = Person.SalutationEnum;
import ReceptionTypeEnum = RoleSetting.ReceptionTypeEnum;

interface SalutationOption {
	value: SalutationEnum;
	label: string;
}

interface UviOption {
	value: ReceptionTypeEnum;
	label: string;
}

/**
 * Eine versendbare Rollenzeile: die Rolle kann einen Portal-Zugang bekommen und hat noch keinen.
 * Registrierte Rollen tauchen hier nicht auf — für sie wäre ein weiterer Code sinnlos.
 */
interface PortalAccessRow {
	group: FormGroup;
	checked: boolean;
	/** Neuer Code, vorhandener Code oder erneuter Versand — bestimmt Tooltip, Zusatztext und Folgen-Zeile. */
	action: PortalAccessAction;
	/** Erklärt in der Zelle, was das Häkchen auslöst (die Rolle nennt die Zeile selbst). */
	tooltip: string;
}

/**
 * Zustand der Portal-Spalte EINER Rollenzeile. `row` gesetzt = versendbar (Häkchen); sonst erklärt
 * `kind`, warum nicht: Rollentyp kann keinen Zugang haben, Rolle ist registriert, Stand lädt noch
 * oder liess sich nicht laden.
 */
interface PortalAccessCell {
	kind: 'sendable' | 'registered' | 'unavailable' | 'loading' | 'failed';
	row?: PortalAccessRow;
	tooltip?: string;
}

/** Tooltip der Portal-Zelle je Aktion — was genau das Häkchen auslöst. */
const PORTAL_ACCESS_TOOLTIP_KEYS: Record<PortalAccessAction, string> = {
	new: 'tenantunitDetails.editor.sendPortalAccess',
	existing: 'tenantunitDetails.editor.sendExistingPortalAccess',
	resend: 'tenantunitDetails.editor.resendPortalAccess',
};

/** Stabile leere Zeilenliste — verhindert eine neue Array-Referenz je Change Detection. */
const NO_ROWS: AraInlineEditRow[] = [];

/** Personen-/Rollen-Editor als ein großer Single-Page-Dialog (Nutzerwechsel). */
@Component({
	selector: 'ara-person-editor-dialog',
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './person-editor-dialog.component.html',
	styleUrl: './person-editor-dialog.component.scss',
	animations: [fadeInUp400ms],
	imports: [
		NgTemplateOutlet,
		AraCheckboxComponent,
		AraSlideToggleComponent,
		AraGenericDialogComponent,
		AraInlineEditTableComponent,
		AraPanelComponent,
		TranslocoDirective,
		ReactiveFormsModule,
		FormsModule,
		MatFormField,
		MatLabel,
		MatError,
		MatSuffix,
		MatSelect,
		MatOption,
		MatInput,
		MatIcon,
		AraButtonComponent,
		MatDatepicker,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatTooltip,
		MatAutocomplete,
		MatAutocompleteTrigger,
		NgxMatSelectSearchModule,
	],
	providers: [
		{ provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS] },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
		{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } as MatFormFieldDefaultOptions },
		StrandBoundaryService,
	],
})
export class PersonEditorDialogComponent implements OnInit {
	private readonly fb = inject(FormBuilder);
	private readonly dialogRef = inject<MatDialogRef<PersonEditorDialogComponent, PersonEditorDialogResult>>(MatDialogRef);
	private readonly destroyRef = inject(DestroyRef);
	private readonly editor = inject(PersonRolesEditorService);
	private readonly snackBar = inject(SnackbarService);
	private readonly transloco = inject(TranslocoService);
	private readonly dateUtil = inject(DateUtil);
	private readonly sessionService = inject(SessionService);
	private readonly serviceCompanyService = inject(ServiceCompanyService);
	private readonly boundaries = inject(StrandBoundaryService);
	private readonly strandContextFactory = inject(StrandContextFactory);
	private readonly portalAccessService = inject(RolePortalAccessService);
	protected readonly data = inject<PersonEditorDialogData>(MAT_DIALOG_DATA);

	protected readonly UiRoleType = UiRoleType;

	// Zell-Templates der Rollen-Tabelle (ara-inline-edit-table) — im roleColumns-computed zu Spalten zusammengesetzt.
	private readonly fromCellTpl = viewChild<TemplateRef<unknown>>('fromCell');
	private readonly toCellTpl = viewChild<TemplateRef<unknown>>('toCell');
	private readonly uviCellTpl = viewChild<TemplateRef<unknown>>('uviCell');
	private readonly tenantNumberCellTpl = viewChild<TemplateRef<unknown>>('tenantNumberCell');
	private readonly portalAccessCellTpl = viewChild<TemplateRef<unknown>>('portalCell');

	/** Spalten der Rollen-Tabelle; leer bis alle Zell-Templates da sind (verhindert Flackern). */
	protected readonly roleColumns = computed<AraInlineEditColumn[]>(() => {
		const from = this.fromCellTpl();
		const to = this.toCellTpl();
		const uvi = this.uviCellTpl();
		const tenantNumber = this.tenantNumberCellTpl();
		const portalAccess = this.portalAccessCellTpl();
		if (!from || !to || !uvi || !tenantNumber || !portalAccess) {
			return [];
		}
		return [
			{ key: 'validFrom', header: 'tenantunitDetails.roleBeginDate', cellTemplate: from, width: '11rem' },
			{ key: 'validTo', header: 'tenantunitDetails.roleEndDate', cellTemplate: to, width: '11rem' },
			{ key: 'uviReception', header: 'tenantunitDetails.uviOptions', cellTemplate: uvi, width: '11rem' },
			{ key: 'tenantNumber', header: 'tenantunitDetails.tenantNumber', cellTemplate: tenantNumber, width: '9rem' },
			{ key: 'portalAccess', header: 'tenantunitDetails.editor.portalAccessColumn', cellTemplate: portalAccess, width: '9rem' },
		];
	});

	protected readonly personForm = this.fb.group({
		salutation: this.fb.control<SalutationEnum | null>(undefined, Validators.required),
		title: this.fb.control<string | null>(''),
		givenName: this.fb.control<string | null>('', [Validators.minLength(1), Validators.maxLength(127)]),
		familyName: this.fb.control<string | null>('', [Validators.minLength(1), Validators.maxLength(127)]),
		fullName: this.fb.control<string | null>('', [Validators.minLength(1), Validators.maxLength(255)]),
		email: this.fb.control<string | null>('', [Validators.email]),
	});

	protected readonly addressForm = this.fb.group({
		street: this.fb.control<string | null>(''),
		houseNumber: this.fb.control<string | null>(''),
		zipCode: this.fb.control<string | null>(''),
		city: this.fb.control<string | null>(''),
		country: this.fb.control<CountryEnum | null>(null),
	});

	protected readonly rolesArray = this.fb.array<FormGroup>([]);

	protected readonly entryContext: PersonEditorEntryContext = this.data.entryContext;
	private readonly uviRights: UviReceptionRights = this.data.uviReceptionRights ?? NO_UVI_RECEPTION_RIGHTS;
	private readonly isPropertyManagement: boolean = this.data.isPropertyManagement ?? false;
	protected readonly showAddress = signal(false);
	protected readonly sendPortalAccess = signal(false);
	/** Portal-Stand je Rollen-Id, vom Server. Neue Rollenzeilen kommen darin nicht vor. */
	private readonly portalAccessByRole = signal(new Map<string, RolePortalAccess>());
	protected readonly portalStatusLoading = signal(false);
	protected readonly portalStatusFailed = signal(false);
	protected readonly saving = signal(false);
	protected readonly isExistingPerson = signal(false);
	protected readonly knownPersonControl = this.fb.control<PersonEditorPersonOption | null>(null);
	protected readonly countryFilterControl = this.fb.control<string>('');
	private readonly defaultCountry = signal<CountryEnum | null>(null);

	private readonly recalcTick = signal(0);
	/** Zeilen, deren Häkchen der Nutzer selbst gesetzt hat — die fasst die E-Mail-Automatik nicht mehr an. */
	private readonly manualPortalChoice = new WeakSet<FormGroup>();
	/** Vorzustand des E-Mail-Feldes: nur der Übergang leer → gefüllt hakt automatisch an (Fall 2). */
	private emailWasEmpty = true;
	private isEdit = false;
	private person: Person = { address: this.editor.getEmptyAddress() };
	private hasPersistError = false;

	protected readonly salutations: SalutationOption[] = [
		{ value: SalutationEnum.UNKNOWN, label: this.transloco.translate('user.unknown') },
		{ value: SalutationEnum.MR, label: this.transloco.translate('user.mr') },
		{ value: SalutationEnum.MRS, label: this.transloco.translate('user.ms') },
		{ value: SalutationEnum.DIV, label: this.transloco.translate('user.div') },
		{ value: SalutationEnum.FAMILY, label: this.transloco.translate('user.family') },
		{ value: SalutationEnum.COMPANY, label: this.transloco.translate('user.company') },
	];
	protected readonly titleOptions = ['Dr.', 'Prof.', 'Prof. Dr.'];
	private readonly countries: TranslatedCountry[] = this.editor.buildCountryOptions();
	protected readonly filteredCountries = signal<TranslatedCountry[]>(this.countries);
	protected readonly uviOptions: UviOption[] = this.buildUviOptions();

	/** Stabile Datepicker-Filter je Rollenzeile (verhindert Re-Render des Kalenders bei jeder CD). */
	private readonly dateFilterCache = new WeakMap<FormGroup, (date: Dayjs | null) => boolean>();
	/** Stabile uVI-Optionslisten je Rollenzeile (siehe {@link uviOptionsFor}). */
	private readonly uviOptionsCache = new WeakMap<FormGroup, { value: ReceptionTypeEnum | null; options: UviOption[] }>();

	// -- computed view state ----------------------------------------------------------------------

	private readonly roleValues = computed<PersonEditorRoleValue[]>(() => {
		this.recalcTick();
		return this.rolesArray.getRawValue() as unknown as PersonEditorRoleValue[];
	});

	protected readonly strandContexts = signal<StrandContext[]>(this.data.strandContexts ?? []);

	protected readonly isVacancyOnly = computed(() => {
		const roles = this.roleValues();
		return roles.length > 0 && roles.every((role) => role.roleType === UiRoleType.VACANCY);
	});
	protected readonly showPerson = computed(() => !this.isVacancyOnly() && this.entryContext !== 'vacancy');

	/** Ohne das Recht auf Eigentümerdaten ist der Dialog lesend, sobald eine Eigentümer-Rolle im Spiel ist. */
	protected readonly protectedRoleLocked = computed(() => {
		if (this.isPropertyManagement) {
			return false;
		}
		if (this.entryContext === 'owner') {
			return true;
		}
		return this.roleValues().some((role) => isProtectedRoleType(role.roleType));
	});

	/** Dialog-Titel nach Modus: Leerstand / bestehende Person bearbeiten / neue Person anlegen. */
	protected readonly dialogTitleKey = computed<string>(() => {
		const vacancyOnly = this.isVacancyOnly();
		const existing = this.isExistingPerson();
		if (this.entryContext === 'vacancy' || vacancyOnly) {
			// Beim Bearbeiten eines vorhandenen Leerstands nicht „anlegen" behaupten.
			return this.entryContext === 'edit' ? 'tenantunitDetails.editor.titleEditVacancy' : 'tenantunitDetails.editor.titleVacancy';
		}
		return existing ? 'tenantunitDetails.editor.titleEditPerson' : 'tenantunitDetails.editor.titleNewPerson';
	});

	/** Pflicht nur beim uVI-Empfänger — dort ist die Zustelladresse der Versandweg der Verbrauchsinformation. */
	protected readonly addressRequired = computed(() => this.roleValues().some((role) => role.roleType === UiRoleType.CONSUMPTION_INFO_RECEIVER));
	/** Eine Zustelladresse haben Eigentümer, Mieter und uVI-Empfänger; Bewohner wohnen in der Einheit, Leerstand hat keine Person. */
	protected readonly addressToggleVisible = computed(() => {
		if (this.isVacancyOnly()) {
			return false;
		}
		return this.roleValues().some(
			(role) => role.roleType === UiRoleType.OWNER || role.roleType === UiRoleType.TENANT || role.roleType === UiRoleType.CONSUMPTION_INFO_RECEIVER
		);
	});
	private readonly addressHasInput = computed(() => {
		this.recalcTick();
		const values = this.addressForm.getRawValue();
		return !!(values.street?.trim() || values.houseNumber?.trim() || values.zipCode?.trim() || values.city?.trim());
	});

	protected readonly addressVisible = computed(() => {
		const required = this.addressRequired();
		const shown = this.showAddress();
		const hasInput = this.addressHasInput();
		return required || ((shown || hasInput) && this.addressToggleVisible());
	});

	/**
	 * Rollen, die noch keinen Portal-Zugang haben — genau die bekommen ein Häkchen in ihrer Zeile.
	 * Registrierte Rollen fallen heraus (Fall 1), uVI-Empfänger und Leerstand können keinen Zugang haben.
	 */
	protected readonly portalAccessRows = computed<PortalAccessRow[]>(() => {
		this.recalcTick();
		const email = this.personForm.controls.email.value?.trim() ?? '';
		return this.roleGroups()
			.filter((group) => canHavePortalAccess(this.roleTypeOf(group)) && !this.portalStatusOf(group)?.registered)
			.map((group) => {
				const action = this.portalAccessAction(this.portalStatusOf(group), email);
				return {
					group,
					checked: !!group.get('portalAccess')?.value,
					action,
					tooltip: this.transloco.translate(PORTAL_ACCESS_TOOLTIP_KEYS[action]),
				};
			});
	});

	/** Portal-Spalte je Rollenzeile — eine Objektidentität pro Neuberechnung, nicht pro Change Detection. */
	private readonly portalAccessCells = computed<Map<FormGroup, PortalAccessCell>>(() => {
		const rowByGroup = new Map(this.portalAccessRows().map((row) => [row.group, row]));
		return new Map(this.roleGroups().map((group) => [group, this.buildPortalAccessCell(group, rowByGroup.get(group))]));
	});

	protected readonly portalAccessSelected = computed(() => this.portalAccessRows().some((row) => row.checked));

	protected readonly validation = computed(() => {
		this.boundaries.revision();
		const roles = this.roleValues();
		return this.editor.validate(roles, this.strandContexts(), this.boundaries.overrides(roles));
	});
	protected readonly consequences = computed(() => {
		this.boundaries.revision();
		const roles = this.roleValues();
		return this.editor.buildConsequences({
			roles,
			strands: this.strandContexts(),
			boundaryOverrides: this.boundaries.overrides(roles),
			hasMail: !!this.personForm.controls.email.value?.trim(),
			portalAccessActionByRoleId: this.portalAccessActions(),
			isEdit: this.isExistingPerson(),
		});
	});

	protected readonly canSave = computed(() => {
		this.recalcTick();
		const personValid = this.isPersonValid();
		const addressValid = this.isAddressValid();
		const rolesValid = this.areRolesValid();
		const datesWithinStrand = this.areRoleDatesWithinStrand();
		const noHardErrors = this.validation().hard.length === 0;
		const ownerLocked = this.protectedRoleLocked();
		return personValid && addressValid && rolesValid && datesWithinStrand && noHardErrors && !this.saving() && !ownerLocked;
	});

	protected readonly actions = computed<AraGenericDialogAction[]>(() => {
		const list: AraGenericDialogAction[] = [];
		if (this.protectedRoleLocked()) {
			return list;
		}
		// Nicht anbieten, wenn schon ein Leerstand bearbeitet/angelegt wird.
		if (!this.isVacancyOnly() && this.entryContext !== 'vacancy') {
			list.push({
				labelKey: 'tenantunitDetails.editor.discardAndVacancy',
				disabled: () => this.saving(),
				handler: () => this.discardAndVacancy(),
			});
		}
		// Die Rollen werden nacheinander geschrieben — ohne Spinner sieht das nach "nichts passiert" aus.
		list.push({
			labelKey: 'commonActions.save',
			icon: 'save',
			color: 'primary',
			disabled: () => !this.canSave(),
			loading: () => this.saving(),
			handler: () => this.save(),
		});
		return list;
	});

	ngOnInit(): void {
		this.applyMailValidators(this.sendPortalAccess());
		this.boundaries.init(this.strandContexts());
		this.initFromEntryContext();
		this.loadPortalAccess();
		this.setupSubscriptions();
		this.prefillCountryFromServiceCompany();
		this.bumpRecalc();
	}

	// -- initialisation ---------------------------------------------------------------------------

	private initFromEntryContext(): void {
		switch (this.entryContext) {
			case 'resident':
			case 'owner':
			case 'tenant':
			case 'vacancy':
				this.addRoleGroup(this.contextRoleType());
				break;
			case 'newPerson':
				this.applyPreselectedRolesOrDefault(UiRoleType.RESIDENT);
				break;
			case 'edit':
				// Platzhalter-Ids (`vacancy0`, `new0`) sind keine bestehende Person → kein updatePerson().
				this.isEdit = !!this.data.person?.id && !isSyntheticPersonId(this.data.person.id);
				this.isExistingPerson.set(this.isEdit);
				if (this.data.person) {
					this.person = structuredClone(this.data.person);
					this.fillPersonControls(this.person);
				}
				this.applyExistingRoles();
				break;
		}
	}

	protected isNewRole(group: FormGroup): boolean {
		return !group.get('id')?.value;
	}

	/** Übernimmt das Einzugsdatum (von/seit) dieser Zeile auf alle anderen editierbaren Rollen. */
	protected copyMoveInToAllRoles(source: FormGroup): void {
		const date = source.get('validFrom')?.value as Dayjs | null;
		if (!date) {
			return;
		}
		for (const group of this.roleGroups()) {
			const control = group.get('validFrom');
			if (group !== source && control && !control.disabled) {
				control.setValue(date);
				this.boundaries.pull(this.roleTypeOf(group), date);
			}
		}
		this.bumpRecalc();
	}

	// -- Strang-Kontextzeilen ----------------------------------------------------------------------

	protected boundaryControl(roleId: string): FormControl<Dayjs | null> {
		return this.boundaries.control(roleId);
	}

	protected isBoundaryEditable(strandType: RoleType): boolean {
		return this.boundaries.isEditable(strandType);
	}

	protected minValidFrom(group: FormGroup): Dayjs | null {
		return this.boundaries.minValidFrom(this.roleTypeOf(group));
	}

	protected maxValidFrom(group: FormGroup): Dayjs | null {
		return this.boundaries.maxValidFrom(this.roleTypeOf(group));
	}

	/** Setzt das Von einer Zeile und zieht im Belegungsstrang die Vorgängergrenze mit. */
	protected setRoleValidFrom(group: FormGroup, date: Dayjs | null): void {
		group.get('validFrom')?.setValue(date);
		this.boundaries.pull(this.roleTypeOf(group), date);
		this.bumpRecalc();
	}

	/** Einmal pro Änderung gebaut — ein neues Array je Change Detection würde die Tabelle neu binden. */
	private readonly strandRowsByType = computed<Map<RoleType, AraInlineEditRow[]>>(() => {
		this.recalcTick();
		const byType = new Map<RoleType, AraInlineEditRow[]>();
		for (const group of this.roleGroups()) {
			const roleType = this.roleTypeOf(group);
			if (!roleType) {
				continue;
			}
			const strandType = StrandContextFactory.strandTypeOf(roleType);
			const rows = byType.get(strandType) ?? [];
			rows.push({ group });
			byType.set(strandType, rows);
		}
		return byType;
	});

	/** Stränge, für die diese Person Zeilen hat — je Strang eine Gruppe im Dialog. */
	protected readonly visibleStrands = computed<StrandContext[]>(() =>
		[...this.strandRowsByType().keys()].map((strandType) => this.boundaries.strandFor(strandType) ?? { roleType: strandType })
	);

	/** Zeilen dieser Person im gegebenen Strang; Bewohner und Leerstand gemeinsam. */
	protected strandRows(strandType: RoleType): AraInlineEditRow[] {
		return this.strandRowsByType().get(strandType) ?? NO_ROWS;
	}

	private contextRoleType(): RoleType {
		switch (this.entryContext) {
			case 'owner':
				return UiRoleType.OWNER;
			case 'tenant':
				return UiRoleType.TENANT;
			case 'vacancy':
				return UiRoleType.VACANCY;
			default:
				return UiRoleType.RESIDENT;
		}
	}

	private applyPreselectedRolesOrDefault(fallback: RoleType): void {
		const preselected = (this.data.roles ?? []).map((role) => role.roleType).filter((type): type is RoleType => !!type && type !== UiRoleType.VACANCY);
		const unique = [...new Set(preselected)];
		if (unique.length === 0) {
			this.addRoleGroup(fallback);
			return;
		}
		for (const type of unique) {
			this.addRoleGroup(type);
		}
	}

	private applyExistingRoles(): void {
		this.loadExistingRoles(this.data.roles ?? []);
	}

	private loadExistingRoles(existing: Role[]): void {
		if (existing.length === 0) {
			this.addRoleGroup(UiRoleType.RESIDENT);
			return;
		}
		for (const role of existing) {
			this.rolesArray.push(
				this.createRoleGroup({
					id: role.id ?? null,
					roleType: role.roleType ?? null,
					// minDate ist die Backend-Darstellung von „seit immer" (siehe buildRoleDto) — dann gehört
					// das Feld leer, statt ein Startdatum 01.01.1970 zu behaupten. Analog zu validTo/maxDate.
					validFrom: role.validFrom && !dayjs(role.validFrom).isSame(DateUtil.minDate, 'day') ? dayjs(role.validFrom) : null,
					validTo: role.validTo && !dayjs(role.validTo).isSame(DateUtil.maxDate, 'day') ? dayjs(role.validTo) : null,
					// Kein PRINT-Fallback: der würde ein vorhandenes PORTAL/NO_EED beim Speichern ersetzen.
					uviReception: role.uviReception ?? null,
					uviReceptionInitial: role.uviReception ?? null,
					tenantNumber: role.propertyManagementNumber ?? null,
				})
			);
		}
	}

	private fillPersonControls(person: Person): void {
		const names = this.editor.splitFullName(person);
		this.personForm.patchValue({
			salutation: person.salutation ?? SalutationEnum.UNKNOWN,
			title: person.title ?? '',
			givenName: names.givenName,
			familyName: names.familyName,
			fullName: person.fullName ?? '',
			email: person.email ?? '',
		});
		// Vorbelegte Adresse ist KEINE Nutzereingabe: sie darf das Haekchen nicht anhaken (Fall 3/4).
		this.emailWasEmpty = !person.email?.trim();
		this.applyMailValidators(false);
		const address = person.address;
		const street = address?.streets?.[0];
		const country = address?.country ?? this.defaultCountry();
		this.addressForm.patchValue({
			street: street?.streetName ?? '',
			houseNumber: street?.houseNumber ?? '',
			zipCode: address?.zipCode ?? '',
			city: address?.city ?? '',
			country,
		});
		if (this.hasExistingAddress(address)) {
			this.showAddress.set(true);
		}
		this.applyZipValidators(country);
	}

	private hasExistingAddress(address: Address | undefined): boolean {
		const street = address?.streets?.[0];
		return !!(address?.city || address?.zipCode || street?.streetName || street?.houseNumber);
	}

	private setupSubscriptions(): void {
		this.rolesArray.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.bumpRecalc();
			// Eine neu hinzugefügte Rolle soll dieselbe E-Mail-Automatik erben wie die bestehenden Zeilen.
			this.syncPortalAccessWithEmail();
		});
		this.personForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.bumpRecalc();
			this.syncPortalAccessWithEmail();
		});
		this.addressForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.bumpRecalc());

		this.countryFilterControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((search) => {
			this.filteredCountries.set(this.editor.filterCountries(this.countries, search));
		});
	}

	private bumpRecalc(): void {
		this.recalcTick.update((value) => value + 1);
	}

	// -- roles ------------------------------------------------------------------------------------

	private createRoleGroup(value: Partial<PersonEditorRoleValue>): FormGroup {
		const group = this.fb.group({
			id: this.fb.control<string | null>(value.id ?? null),
			roleType: this.fb.control<RoleType | null>({ value: value.roleType ?? null, disabled: !!value.id }, Validators.required),
			// Der required-Validator kommt aus applyStartDateRules() (am Ende von applyRoleTypeRules) —
			// beim ersten Bewohner einer Nutzeinheit ist das Von-Datum optional.
			validFrom: this.fb.control<Dayjs | null>(value.validFrom ?? null),
			validTo: this.fb.control<Dayjs | null>(value.validTo ?? null),
			uviReception: this.fb.control<string | null>(value.uviReception ?? null),
			// Baseline (nie im Template gebunden): entscheidet beim Speichern, ob geschrieben wird.
			uviReceptionInitial: this.fb.control<string | null>(value.uviReceptionInitial ?? null),
			tenantNumber: this.fb.control<string | null>(value.tenantNumber ?? null),
			// Nie im Template gebunden (das Häkchen sitzt beim E-Mail-Feld) — reist aber mit der Rolle ins Speichern.
			portalAccess: this.fb.control<boolean>(value.portalAccess ?? false),
		});
		this.applyRoleTypeRules(group, value.roleType ?? null);
		return group;
	}

	private addRoleGroup(roleType: RoleType | null): void {
		const group = this.createRoleGroup({ roleType });
		this.rolesArray.push(group);
	}

	/** Fügt eine Rolle des gewählten Typs hinzu (Button-Aktion; Leerstand ersetzt alle anderen Rollen). */
	protected addRoleType(roleType: RoleType): void {
		if (!roleType) {
			return;
		}
		if (roleType === UiRoleType.VACANCY) {
			this.rolesArray.clear();
		}
		this.addRoleGroup(roleType);
	}

	protected removeRole(index: number): void {
		this.rolesArray.removeAt(index);
	}

	protected roleGroups(): FormGroup[] {
		return this.rolesArray.controls;
	}

	protected roleTypeOf(group: FormGroup): RoleType | null {
		return group.get('roleType')?.value ?? null;
	}

	/** Eindeutiger, stabiler Track-Key je Rollenzeile: bestehende Rolle über ihre id, neue Zeilen über den Index. */
	protected roleRowKey(group: FormGroup, index: number): string | number {
		return (group.get('id')?.value as string | null) ?? index;
	}

	protected showKnownPersonSelect(): boolean {
		// Beim Bearbeiten einer konkreten Person NIE eine andere Person wählbar machen — nur beim Anlegen.
		return this.entryContext !== 'edit' && (this.data.personOptions?.length ?? 0) > 0 && this.showPerson();
	}

	protected roleControl(group: FormGroup, name: string): FormControl {
		return group.get(name) as FormControl;
	}

	private applyRoleTypeRules(group: FormGroup, roleType: RoleType | null): void {
		this.applyVacancyDateLocks(group, roleType);
		this.applyResidentUviDefault(group, roleType);
		this.applyStartDateRules(group, roleType);
		this.applyChainTailLocks(group, roleType);
	}

	/**
	 * Das Backend verarbeitet Datumsänderungen nur an der zuletzt beginnenden Rolle eines Zeitstrangs.
	 * Alles davor — etwa ein Leerstand, auf den schon eine Rolle folgt — ist damit unveränderlich.
	 */
	private isNonLatestRole(roleId: string | null, roleType: RoleType | null): boolean {
		if (!roleId || !roleType) {
			return false;
		}
		const latest = this.data.latestRoleIdByStrand?.get(StrandContextFactory.strandTypeOf(roleType));
		return !!latest && latest !== roleId;
	}

	protected isNonLatestRoleLocked(group: FormGroup): boolean {
		return this.isNonLatestRole(group.get('id')?.value ?? null, this.roleTypeOf(group));
	}

	private applyChainTailLocks(group: FormGroup, roleType: RoleType | null): void {
		if (!this.isNonLatestRole(group.get('id')?.value ?? null, roleType)) {
			return;
		}
		group.get('validFrom')?.disable({ emitEvent: false });
		group.get('validTo')?.disable({ emitEvent: false });
	}

	/**
	 * Bei der ERSTEN Rolle eines Typs (Bewohner, Eigentümer, Mieter, …) ist das Von-Datum optional —
	 * beim Erstanlegen ist das Startdatum oft unbekannt. Bleibt es leer, startet die Rolle „seit immer"
	 * (siehe {@code PersonRolesEditorService.persist()}, das auf {@code DateUtil.minDate} zurückfällt).
	 * Ab der zweiten Rolle desselben Typs bleibt es Pflicht, sonst ließe sich die Kette nicht
	 * fortschreiben.
	 */
	private isStartDateOptional(roleType: RoleType | null, ownRoleId: string | null): boolean {
		if (!roleType) {
			return false;
		}
		return !(this.data.roleIdsByType?.get(roleType) ?? []).some((id) => id !== ownRoleId);
	}

	private applyStartDateRules(group: FormGroup, roleType: RoleType | null): void {
		const validFrom = group.get('validFrom');
		validFrom?.setValidators(this.isStartDateOptional(roleType, group.get('id')?.value ?? null) ? [] : [Validators.required]);
		validFrom?.updateValueAndValidity({ emitEvent: false });
	}

	/** Leerstand: Enddatum immer gesperrt, Beginn nur bei Bestandszeilen. */
	private applyVacancyDateLocks(group: FormGroup, roleType: RoleType | null): void {
		const validTo = group.get('validTo');
		if (roleType !== UiRoleType.VACANCY) {
			validTo?.enable({ emitEvent: false });
			return;
		}
		if (this.isNewRow(group)) {
			// Nur NEUE Leerstände leeren — sonst verliert ein beendeter Leerstand sein Enddatum.
			validTo?.setValue(null, { emitEvent: false });
		} else {
			// Beginn ergibt sich aus dem Ende der Vorgänger-Rolle; eine Änderung wurde nie gespeichert.
			group.get('validFrom')?.disable({ emitEvent: false });
		}
		validTo?.disable({ emitEvent: false });
	}

	/** Default beim Anlegen. */
	private applyResidentUviDefault(group: FormGroup, roleType: RoleType | null): void {
		const uvi = group.get('uviReception');
		uvi?.clearValidators();
		if (roleType === UiRoleType.RESIDENT && this.isNewRow(group) && !uvi?.value) {
			uvi?.setValue(ReceptionTypeEnum.PRINT, { emitEvent: false });
		}
		uvi?.updateValueAndValidity({ emitEvent: false });
	}

	private isNewRow(group: FormGroup): boolean {
		return !group.get('id')?.value;
	}

	/** Im Add-Select wählbare Rollentypen (Multiplizität: Anlegen je Typ einmal, Edit mehrfach). */
	protected availableAddRoleTypes(): RoleType[] {
		const current = this.roleValues();
		if (current.some((role) => role.roleType === UiRoleType.VACANCY)) {
			return [];
		}
		const selected = new Set(current.map((role) => role.roleType));
		return this.data.availableRoleTypes.filter((roleType) => {
			if (isProtectedRoleType(roleType) && !this.isPropertyManagement) {
				return false;
			}
			if (roleType === UiRoleType.VACANCY) {
				return current.length === 0;
			}
			if (!this.isEdit && selected.has(roleType)) {
				return false;
			}
			return !(this.isEdit && roleType === UiRoleType.RESIDENT && this.hasOpenRoleOfType(UiRoleType.RESIDENT));
		});
	}

	private hasOpenRoleOfType(roleType: RoleType): boolean {
		return this.roleValues().some((role) => role.roleType === roleType && !role.validTo);
	}

	protected isResidentEndLocked(group: FormGroup): boolean {
		return this.roleTypeOf(group) === UiRoleType.RESIDENT;
	}

	protected isVacancyStartLocked(group: FormGroup): boolean {
		return this.roleTypeOf(group) === UiRoleType.VACANCY && !!group.get('id')?.value;
	}

	protected showTenantNumberCell(group: FormGroup): boolean {
		return this.roleTypeOf(group) === UiRoleType.RESIDENT;
	}

	protected showAddressCell(group: FormGroup): boolean {
		const type = this.roleTypeOf(group);
		return type === UiRoleType.OWNER || type === UiRoleType.TENANT;
	}

	/** Datepicker-Filter je Rollenzeile: nur Tage in verfügbaren Perioden des Rollentyps sind wählbar. */
	protected dateFilterFor(group: FormGroup): (date: Dayjs | null) => boolean {
		const cached = this.dateFilterCache.get(group);
		if (cached) {
			return cached;
		}
		const roleType = group.get('roleType')?.value as RoleType | undefined;
		const filter = (date: Dayjs | null): boolean => {
			if (!date) {
				return false;
			}
			if (!roleType || roleType === UiRoleType.CONSUMPTION_INFO_RECEIVER || roleType === UiRoleType.VACANCY) {
				return true;
			}
			const periods = this.data.allAvailablePeriodsByRoleType?.get(roleType) ?? [];
			if (periods.length === 0) {
				return true;
			}
			return periods.some((period) => date.isSameOrAfter(period.start, 'day') && date.isSameOrBefore(period.end, 'day'));
		};
		this.dateFilterCache.set(group, filter);
		return filter;
	}

	protected getRoleTypeLabel(roleType: string | null | undefined): string {
		return this.editor.getRoleTypeLabel(roleType);
	}

	// -- person / address helpers -----------------------------------------------------------------

	protected isFullNameOnly(): boolean {
		return this.editor.isFullNameOnlySalutation(this.personForm.controls.salutation.value);
	}

	protected onPortalAccessToggle(row: PortalAccessRow, change: MatCheckboxChange): void {
		// Ab jetzt entscheidet der Nutzer über diese Zeile, nicht mehr das E-Mail-Feld.
		this.manualPortalChoice.add(row.group);
		row.group.get('portalAccess')?.setValue(change.checked);
		this.applyMailValidators(this.portalAccessSelected());
		this.bumpRecalc();
	}

	/** Fall 2: mit der ersten eingegebenen Adresse hakt sich der Versand selbst an, beim Leeren wieder ab. */
	private syncPortalAccessWithEmail(): void {
		const hasEmail = !!this.personForm.controls.email.value?.trim();
		const crossedEmptyBoundary = hasEmail === this.emailWasEmpty;
		this.emailWasEmpty = !hasEmail;
		if (!crossedEmptyBoundary) {
			return;
		}
		for (const row of this.portalAccessRows()) {
			// Ein bereits verschickter Code wird nie automatisch nochmal verschickt.
			if (row.action === 'resend' || this.manualPortalChoice.has(row.group)) {
				continue;
			}
			row.group.get('portalAccess')?.setValue(hasEmail, { emitEvent: false });
		}
		this.bumpRecalc();
		this.applyMailValidators(this.portalAccessSelected());
	}

	/** Was je angehakter Rolle mit dem Code passiert — Grundlage der Folgen-Zeilen. */
	private portalAccessActions(): ReadonlyMap<string, PortalAccessAction> {
		const actions = new Map<string, PortalAccessAction>();
		for (const row of this.portalAccessRows()) {
			const roleId = row.group.get('id')?.value as string | null;
			if (roleId) {
				actions.set(roleId, row.action);
			}
		}
		return actions;
	}

	/**
	 * Vorhandene Codes der angehakten Bestandsrollen: die werden erneut verschickt statt neu angelegt.
	 * Neue Rollen und Rollen ohne offenen Code fehlen hier — für sie entsteht ein frischer Code.
	 */
	private portalAccessCodes(): Record<string, string> {
		const codes: Record<string, string> = {};
		for (const row of this.portalAccessRows()) {
			const roleId = row.group.get('id')?.value as string | null;
			const openCode = roleId ? this.portalAccessByRole().get(roleId)?.openCode : undefined;
			if (row.checked && roleId && openCode) {
				codes[roleId] = openCode;
			}
		}
		return codes;
	}

	/**
	 * Ohne offenen Code entsteht einer; mit offenem Code wird DIESER verschickt — „erneut" nur, wenn er
	 * schon an genau diese Adresse ging. Adressvergleich normalisiert, sonst gilt „Max@x.de" als andere
	 * Adresse als „max@x.de" und der Versand hiesse faelschlich „erstmalig".
	 */
	private portalAccessAction(status: RolePortalAccess | undefined, email: string): PortalAccessAction {
		if (!status?.hasOpenCode) {
			return 'new';
		}
		const notifiedTo = status.lastNotifiedEmail?.trim().toLowerCase();
		return status.lastNotifiedOn && notifiedTo && notifiedTo === email.trim().toLowerCase() ? 'resend' : 'existing';
	}

	private portalStatusOf(group: FormGroup): RolePortalAccess | undefined {
		const roleId = group.get('id')?.value as string | null;
		return roleId ? this.portalAccessByRole().get(roleId) : undefined;
	}

	/** Aus dem Template je Rollenzeile gelesen (Zell-Template der Rollen-Tabelle). */
	protected portalAccessCell(group: FormGroup): PortalAccessCell | undefined {
		return this.portalAccessCells().get(group);
	}

	private buildPortalAccessCell(group: FormGroup, row: PortalAccessRow | undefined): PortalAccessCell {
		if (!canHavePortalAccess(this.roleTypeOf(group))) {
			return { kind: 'unavailable' };
		}
		// Zustand VOR den Zeilen prüfen: ohne geladenen Stand wäre jede registrierte Rolle kurz „versendbar".
		if (this.portalStatusFailed()) {
			return { kind: 'failed', tooltip: this.transloco.translate('tenantunitDetails.editor.portalAccessUnknown') };
		}
		if (this.portalStatusLoading()) {
			return { kind: 'loading' };
		}
		if (this.portalStatusOf(group)?.registered) {
			return { kind: 'registered', tooltip: this.transloco.translate('tenantunitDetails.editor.portalAccessRegisteredHint') };
		}
		return row ? { kind: 'sendable', row, tooltip: row.tooltip } : { kind: 'unavailable' };
	}

	/**
	 * Portal-Stand der Bestandsrollen holen. Ohne ihn liesse sich Fall 1 (registriert) nicht von Fall 3/4
	 * unterscheiden — darum zeigt der Block bis dahin nichts an, statt zu raten.
	 */
	private loadPortalAccess(): void {
		const roleIds = this.roleGroups()
			.map((group) => group.get('id')?.value as string | null)
			.filter((roleId): roleId is string => !!roleId);
		this.portalStatusFailed.set(false);
		if (roleIds.length === 0) {
			this.portalAccessByRole.set(new Map());
			return;
		}
		this.portalStatusLoading.set(true);
		this.portalAccessService
			.loadMany(roleIds)
			.pipe(
				finalize(() => {
					this.portalStatusLoading.set(false);
					this.bumpRecalc();
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: (byRole) => this.portalAccessByRole.set(byRole),
				// Lieber sperren als behaupten: ohne Stand könnte die Zeile „erneut versenden" anbieten,
				// wo nie ein Code verschickt wurde — oder einen Zugang für eine registrierte Rolle.
				error: () => this.portalStatusFailed.set(true),
			});
	}

	private applyMailValidators(required: boolean): void {
		const control = this.personForm.controls.email;
		control.setValidators(required ? [Validators.required, Validators.email] : [Validators.email]);
		control.updateValueAndValidity({ emitEvent: false });
	}

	protected onShowAddressToggle(change: MatSlideToggleChange): void {
		this.showAddress.set(change.checked);
		if (!change.checked) {
			this.clearAddressForm();
		}
		this.bumpRecalc();
	}

	private clearAddressForm(): void {
		const country = this.defaultCountry();
		this.addressForm.patchValue({ street: '', houseNumber: '', zipCode: '', city: '', country });
		this.applyZipValidators(country);
	}

	protected copyRealEstateAddress(): void {
		const address = this.data.realEstateAddress;
		const street = address?.streets?.[0];
		const country = address?.country ?? this.defaultCountry();
		this.addressForm.patchValue({
			street: street?.streetName ?? '',
			houseNumber: street?.houseNumber ?? '',
			zipCode: address?.zipCode ?? '',
			city: address?.city ?? '',
			country,
		});
		this.applyZipValidators(country);
		this.bumpRecalc();
	}

	protected hasRealEstateAddress(): boolean {
		const address = this.data.realEstateAddress;
		return !!(address?.city || address?.zipCode || address?.streets?.[0]?.streetName);
	}

	protected onCountryChange(change: MatSelectChange): void {
		this.applyZipValidators(change.value as CountryEnum | null);
		this.bumpRecalc();
	}

	/**
	 * Belegt das Land der abweichenden Adresse mit dem Land der Servicegesellschaft vor.
	 */
	private prefillCountryFromServiceCompany(): void {
		this.serviceCompanyService
			.getServiceCompany(this.requestArea())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (serviceCompany) => {
					const country = serviceCompany?.company?.address?.country;
					// UNKNOWN ist „keine Angabe" und als Vorbelegung wertlos.
					if (!country || country === CountryEnum.UNKNOWN) {
						return;
					}
					this.defaultCountry.set(country);
					const control = this.addressForm.controls.country;
					if (control.value) {
						return;
					}
					control.setValue(country);
					this.applyZipValidators(country);
					this.bumpRecalc();
				},
				error: () => undefined,
			});
	}

	private applyZipValidators(country: CountryEnum | null): void {
		const control = this.addressForm.controls.zipCode;
		const validators = [Validators.pattern('^[0-9]*$')];
		if (country === CountryEnum.GERMANY) {
			validators.push(Validators.minLength(5), Validators.maxLength(5));
		}
		control.setValidators(validators);
		control.updateValueAndValidity({ emitEvent: false });
	}

	protected selectKnownPerson(change: MatSelectChange): void {
		const selected = change.value as PersonEditorPersonOption | null;
		if (!selected?.person) {
			this.resetToNewPerson();
			return;
		}
		// Die eben angelegten (noch nicht gespeicherten) Rollen überleben den Personenwechsel: der Nutzer
		// hat den Dialog wegen genau dieser Rolle geöffnet und musste sie bisher hinterher erneut anlegen.
		const pendingRoleTypes = this.pendingRoleTypes();
		const existingRoles = selected.roles ?? [];
		this.person = structuredClone(selected.person);
		this.rebuildStrandContexts(this.person.id);
		this.isEdit = this.person.id != null;
		this.isExistingPerson.set(true);
		this.fillPersonControls(this.person);
		this.rolesArray.clear();
		if (existingRoles.length > 0) {
			this.loadExistingRoles(existingRoles);
		}
		this.restorePendingRoles(pendingRoleTypes);
		// Weder Bestands- noch übernommene Rollen: der Dialog braucht eine Zeile zum Arbeiten.
		if (this.rolesArray.length === 0) {
			this.addRoleGroup(UiRoleType.RESIDENT);
		}
		// Andere Person, andere Rollen — der Portal-Stand der vorigen Auswahl gilt hier nicht mehr.
		this.loadPortalAccess();
		this.bumpRecalc();
	}

	/**
	 * Nachbarrollen für die gewählte Person neu bestimmen. Ohne das blieben Grenzen und Datumsfenster
	 * auf dem Stand der zuvor gewählten Person stehen.
	 */
	private rebuildStrandContexts(personId: string | undefined): void {
		const roleModels = this.data.roleModels;
		if (!roleModels) {
			return;
		}
		const strands = this.strandContextFactory.build(roleModels, personId, this.data.availableRoleTypes);
		this.strandContexts.set(strands);
		this.boundaries.reset(strands);
	}

	/** Rollentypen der neuen, noch nicht gespeicherten Zeilen — je Typ einmal, ohne Leerstand. */
	private pendingRoleTypes(): RoleType[] {
		const types = this.roleGroups()
			.filter((group) => this.isNewRole(group))
			.map((group) => this.roleTypeOf(group))
			.filter((roleType): roleType is RoleType => !!roleType && roleType !== UiRoleType.VACANCY);
		return [...new Set(types)];
	}

	/**
	 * Hängt die gemerkten neuen Rollen wieder an — außer einem Bewohner, wenn die gewählte Person dort
	 * bereits eine offene Rolle hat: genau den ließe das Add-Select danach auch nicht mehr zu.
	 */
	private restorePendingRoles(roleTypes: RoleType[]): void {
		for (const roleType of roleTypes) {
			if (roleType === UiRoleType.RESIDENT && this.hasOpenRoleOfType(UiRoleType.RESIDENT)) {
				continue;
			}
			this.addRoleGroup(roleType);
		}
	}

	/** Zurück zum Anlegen einer neuen Person (verwirft die geladene Bestandsperson). */
	private resetToNewPerson(): void {
		this.isEdit = false;
		this.isExistingPerson.set(false);
		this.person = { address: this.editor.getEmptyAddress() };
		this.rebuildStrandContexts(undefined);
		this.knownPersonControl.setValue(null, { emitEvent: false });
		this.personForm.reset({ salutation: SalutationEnum.UNKNOWN, title: '', givenName: '', familyName: '', fullName: '', email: '' });
		this.emailWasEmpty = true;
		this.applyMailValidators(false);
		this.rolesArray.clear();
		this.addRoleGroup(UiRoleType.RESIDENT);
		this.bumpRecalc();
	}

	/** Erklärt im Expertenmodus, warum aktuell keine weitere Rolle hinzufügbar ist (leeres Add-Select). */
	protected addRoleDisabledReason(): string {
		const roles = this.roleValues();
		if (roles.some((role) => role.roleType === UiRoleType.VACANCY)) {
			return this.transloco.translate('tenantunitDetails.editor.addRoleDisabledVacancy');
		}
		return this.transloco.translate('tenantunitDetails.editor.addRoleDisabledAllPresent');
	}

	protected discardAndVacancy(): void {
		this.rolesArray.clear();
		this.addRoleGroup(UiRoleType.VACANCY);
		this.bumpRecalc();
	}

	// -- validation gates -------------------------------------------------------------------------

	private isPersonValid(): boolean {
		// Auch bestehende Personen werden validiert – der Name ist jetzt inline editierbar.
		if (this.isVacancyOnly()) {
			return true;
		}
		const values = this.personForm.getRawValue();
		// Anrede „Ohne" (UNKNOWN) ist erlaubt — es muss nur ein Name vorhanden sein.
		if (this.editor.isFullNameOnlySalutation(values.salutation)) {
			if (!values.fullName?.trim()) {
				return false;
			}
		} else if (!values.familyName?.trim()) {
			return false;
		}
		return this.personForm.controls.email.valid;
	}

	private isAddressValid(): boolean {
		if (!this.addressVisible()) {
			return true;
		}
		const values = this.addressForm.getRawValue();
		const filled = !!values.street?.trim() && !!values.houseNumber?.trim() && !!values.zipCode?.trim() && !!values.city?.trim() && !!values.country;
		return filled && this.addressForm.valid;
	}

	private areRolesValid(): boolean {
		const roles = this.roleValues();
		if (roles.length === 0) {
			return false;
		}
		return roles.every((role) => {
			if (!role.roleType) {
				return false;
			}
			if (!role.validFrom && !this.isStartDateOptional(role.roleType, role.id ?? null) && !this.isNonLatestRole(role.id, role.roleType)) {
				return false;
			}
			// Zustellart nur für NEUE Bewohner-Rollen verlangen — Bestandszeilen dürfen leer sein.
			return role.roleType !== UiRoleType.RESIDENT || !!role.id || !!role.uviReception;
		});
	}

	/**
	 * Ein von Hand getipptes Datum umgeht den Datepicker-Filter und landet als min/max-Fehler am
	 * Control. Ohne diese Prüfung ginge es ans Backend und käme als Fehlermeldung zurück.
	 */
	private areRoleDatesWithinStrand(): boolean {
		return this.roleGroups().every((group) => {
			const control = group.get('validFrom');
			return !control?.hasError('matDatepickerMin') && !control?.hasError('matDatepickerMax');
		});
	}

	// -- save / close -----------------------------------------------------------------------------

	protected close(): void {
		this.dialogRef.close({ saved: false, person: this.person, refreshRequested: this.hasPersistError });
	}

	protected save(): void {
		if (this.protectedRoleLocked()) {
			this.snackBar.error(this.transloco.translate('tenantunitDetails.editor.protectedRoleReadonly'));
			return;
		}
		if (!this.canSave()) {
			this.personForm.markAllAsTouched();
			this.addressForm.markAllAsTouched();
			this.rolesArray.markAllAsTouched();
			this.snackBar.error(this.transloco.translate('errors.checkInput'));
			return;
		}
		this.saving.set(true);
		this.bumpRecalc();
		const person = this.buildPersonFromForm();
		const roles = this.roleValues();

		this.editor
			.persist({
				area: this.requestArea(),
				realEstateId: this.data.currentRealEstateId,
				tenantUnitId: this.data.currentTenantunitId,
				person,
				isEdit: this.isEdit,
				skipPersonPersist: this.isVacancyOnly() && !this.editorHasPersonInput(person),
				roles,
				strands: this.strandContexts(),
				boundaryOverrides: this.boundaries.overrides(this.roleValues()),
				portalAccessCodeByRoleId: this.portalAccessCodes(),
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (result) => {
					this.hasPersistError = false;
					this.snackBar.success(this.transloco.translate(this.savedMessageKey()));
					this.dialogRef.close({ saved: true, person: result.person, roles: result.roles });
				},
				error: (err) => {
					this.saving.set(false);
					this.hasPersistError = true;
					this.bumpRecalc();
					this.snackBar.apiError(err, this.transloco.translate('tenantunitDetails.rolesUpdateError'));
				},
			});
	}

	private editorHasPersonInput(person: Person): boolean {
		return !!(person.fullName?.trim() || person.familyName?.trim() || person.givenName?.trim() || person.email?.trim());
	}

	private savedMessageKey(): string {
		if (this.isVacancyOnly()) {
			return this.roleValues().some((role) => !!role.id) ? 'tenantunitDetails.vacancyUpdated' : 'tenantunitDetails.addedNewVacancy';
		}
		return this.isEdit ? 'tenantunitDetails.personWithRolesUpdated' : 'tenantunitDetails.personWithRolesCreated';
	}

	private buildPersonFromForm(): Person {
		const personValues = this.personForm.getRawValue();
		const addressValues = this.addressForm.getRawValue();
		return this.editor.buildPerson(this.person, {
			salutation: personValues.salutation,
			title: personValues.title,
			givenName: personValues.givenName,
			familyName: personValues.familyName,
			fullName: personValues.fullName,
			email: personValues.email,
			showAddress: this.addressVisible(),
			street: addressValues.street,
			houseNumber: addressValues.houseNumber,
			zipCode: addressValues.zipCode,
			city: addressValues.city,
			country: addressValues.country,
		});
	}

	private requestArea(): number {
		return this.data.currentRealEstateArea ?? this.sessionService.effectiveArea;
	}

	protected realEstateTenantUnitLabel(): string {
		const area = this.data.currentRealEstateArea ?? '-';
		const realEstateNumber = this.data.currentRealEstateNumber ?? '-';
		const tenantUnitNumber = this.data.currentTenantUnitNumber ?? '-';
		const tenantUnitSubNumber = this.data.currentTenantUnitSubNumber ?? '-';
		return `LS ${area}/${realEstateNumber} – NE ${tenantUnitNumber}/${tenantUnitSubNumber}`;
	}

	protected roleDateLabel(value: Dayjs | null | undefined): string {
		return value ? this.dateUtil.checkDateValid(value) : '-';
	}

	/**
	 * Wählbare Zustellarten — PORTAL nur bei aktiviertem Code, „Manuelle Zustellung"/„Keine" nur, wenn
	 * die Liegenschafts-Einstellung sie dem eingeloggten Benutzer freigibt (siehe
	 * {@link UviReceptionRights}). Ohne freigegebene Option bleibt die Zelle rein informativ.
	 */
	private buildUviOptions(): UviOption[] {
		const options: UviOption[] = [];
		if (this.uviRights.canSetPrint) {
			options.push({ value: ReceptionTypeEnum.PRINT, label: this.transloco.translate('uviOptions.PRINT') });
		}
		if (this.uviRights.canSetNone) {
			options.push({ value: ReceptionTypeEnum.NO_EED, label: this.transloco.translate('uviOptions.NO_EED') });
		}
		const permissions = this.data.permissions ?? {};
		const hasActivatedCode = Object.values(permissions).some((list) => list.some((permission) => permission.codeActivatedOn));
		if (hasActivatedCode) {
			options.push({ value: ReceptionTypeEnum.PORTAL, label: this.transloco.translate('uviOptions.PORTAL') });
		}
		return options.sort((a, b) => a.label.localeCompare(b.label));
	}

	/**
	 * Basisliste plus den aktuellen Bestandswert, falls der nicht (mehr) angeboten wird — sonst stellt
	 * `mat-select` ihn leer dar und verwirft ihn beim Speichern. Gecacht je Zeile, weil die Methode aus
	 * dem Template läuft.
	 */
	protected uviOptionsFor(group: FormGroup): UviOption[] {
		// `Role.uviReception` ist lose als string typisiert, ist zur Laufzeit aber ReceptionTypeEnum.
		const current = ((group.get('uviReception')?.value ?? group.get('uviReceptionInitial')?.value) as ReceptionTypeEnum | null) ?? null;
		const cached = this.uviOptionsCache.get(group);
		if (cached?.value === current) {
			return cached.options;
		}
		const options =
			!current || this.uviOptions.some((option) => option.value === current)
				? this.uviOptions
				: [...this.uviOptions, { value: current, label: this.transloco.translate(`uviOptions.${current}`) }];
		this.uviOptionsCache.set(group, { value: current, options });
		return options;
	}

	protected canEditUvi(group: FormGroup): boolean {
		return !this.isNewRow(group) && this.uviOptionsFor(group).length > 1;
	}

	protected uviReadonlyLabel(group: FormGroup): string {
		const current = (group.get('uviReception')?.value ?? group.get('uviReceptionInitial')?.value) as string | null;
		return current ? this.transloco.translate(`uviOptions.${current}`) : '-';
	}

	protected uviReadonlyHintKey(group: FormGroup): string {
		return this.isNewRow(group) ? 'tenantunitDetails.editor.uviSetAfterCreation' : 'tenantunitDetails.editor.uviChangeNotAllowed';
	}
}
